<template>
  <div class="ls-share-row" :class="{ 'ls-share-row--spacer': !isLast }">
    <div class="ls-share-row--checkmark">
      <BTooltip :active="Boolean(shareIsPending)" label="Pending" position="is-top">
        <LsIcon
          size="sm"
          :type="platform.is_share_complete || shareIsPending ? 'checkmark' : 'dashes'"
          :color="platform.is_share_complete ? 'primary' : 'gray-tertiary'"
        />
      </BTooltip>
    </div>
    <div class="ls-share-row--copy">{{ platform.button_text }}</div>
    <div class="ls-share-row--button-container">
      <div v-if="platform.platformName === 'link'" class="ls-share-row--button-link">
        <LsInput
          :value="platform.shortLinkWithDate"
          track-id="`ls_share_row-link`"
          name="shortLinkWithDate"
          class="ls-share-row--input"
          readonly
        />
        <LsClickable track-id="`ls_share_row-link`" test-id="clickable" @click="onCopy">
          <div class="ls-share-row--clickable"><LsIcon size="sm" type="copy" color="white" /></div>
        </LsClickable>
      </div>
      <LsButton
        v-else
        :track-id="`ls_share_row-${platform.platformName}`"
        :icon="platform.buttonIcon"
        :text="platform.cta"
        :bg-color="platform.accentColor"
        class="ls-share-row--button"
        test-id="button"
        @click="shareViaPlatform"
      />
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import { SHARE__SHARE_VIA_PLATFORM, SHARE__PLATFORMS } from '@/store/types';
import { mapActions, mapGetters } from 'vuex';
import { LsButton, LsClickable, LsIcon, LsInput } from '@/components/common';
import VueTypes from 'vue-types';
import {
  lsModalService,
  lsStorageService,
  lsStorageServiceConstants,
  lsToastService,
} from 'ls-vue-common';
import { EmailModal, NextDoorModal } from '@/modals';

export default {
  name: 'LsShareRow',
  components: {
    LsButton,
    LsClickable,
    LsIcon,
    LsInput,
  },
  props: {
    name: VueTypes.string.isRequired,
    isLast: VueTypes.bool.def(false),
  },
  data() {
    return {
      pending: false,
    };
  },
  computed: {
    ...mapGetters([SHARE__PLATFORMS]),
    platform() {
      return this[SHARE__PLATFORMS][this.name];
    },
    shareIsPending() {
      return (
        this.pending ||
        lsStorageService.get(_.get(lsStorageServiceConstants, `SHARE_${_.toUpper(this.name)}`)) ||
        this.platform.share_started
      );
    },
  },
  methods: {
    ...mapActions([SHARE__SHARE_VIA_PLATFORM]),
    shareViaPlatform() {
      const { platformName } = this.platform;
      this.pending = true;

      if (platformName === 'nextdoor' || platformName === 'email') {
        lsModalService.open({
          parent: this,
          component: platformName === 'nextdoor' ? NextDoorModal : EmailModal,
          width: 600,
        });
      } else {
        this[SHARE__SHARE_VIA_PLATFORM]({ platform: this.platform });
      }
    },
    onCopy() {
      this.$copyText(this.platform.shortLinkWithDate).then(this.onCopySuccess, this.onCopyFailure);
    },
    onCopySuccess() {
      this.shareViaPlatform();
      lsToastService.open({
        message: 'Link copied to clipboard',
        type: 'is-success',
      });
    },
    onCopyFailure() {
      lsToastService.open({ message: 'Unable to copy link', type: 'is-error' });
    },
  },
};
</script>

<style lang="scss" scoped>
.ls-share-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.ls-share-row--spacer {
  padding-bottom: 15px;
  margin-bottom: 15px;
  border-bottom: 1px solid $primary-light;
}

.ls-share-row--checkmark {
  display: flex;
  align-items: center;
  height: 24px;
  padding-right: 30px;
  border-right: 1px solid $primary-light;
  margin-right: 30px;
}

.ls-share-row--copy {
  font-size: $size-5;
  font-weight: bold;
}

.ls-share-row--button-container {
  margin-left: auto;
}

.ls-share-row--button-link {
  display: flex;
}

.ls-share-row--button {
  min-width: 225px !important;
}

.ls-share-row--input {
  //override default input spacing
  margin-bottom: -12px;
  width: 192px;
}

.ls-share-row--clickable {
  background-color: $primary;
  width: 31.5px;
  height: 31.5px;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    background-color: $special-primary-hover;
  }
}

@media (max-width: $mobile-max-width) {
  .ls-share-row {
    flex-direction: row-reverse;
  }

  .ls-share-row--button-container {
    flex-basis: 100%;
    margin-top: 20px;
  }

  .ls-share-row--checkmark {
    padding-right: 0;
    padding-left: 30px;
    border-right: none;
    border-left: 1px solid $primary-light;
    margin-right: 0;
    margin-left: 30px;
  }

  .ls-share-row--copy {
    margin-right: auto;
  }

  .ls-share-row--spacer {
    padding-bottom: 30px;
    margin-bottom: 30px;
  }

  .ls-share-row--input {
    width: 100%;
  }
}
.themed-app {
  .ls-share-row--spacer {
    border-color: $theme-color-primary-light;
  }
  .ls-share-row--checkmark {
    border-color: $theme-color-primary-light;
  }
  .ls-share-row--copy {
    font-size: $theme-txt-body;
  }
  .ls-share-row--clickable {
    background-color: $theme-color-primary;
    &:hover {
      background-color: $theme-color-primary-hover;
    }
  }

  @media (max-width: $mobile-max-width) {
    .ls-share-row--checkmark {
      border-color: $theme-color-primary-light;
    }
  }
}
</style>
